import {
  boolean,
  index,
  integer,
  numeric,
  pgTable,
  text,
  timestamp,
  uuid,
  varchar,
  pgEnum,
} from 'drizzle-orm/pg-core';
import { decimal } from '@trashlab/api/src/domains/core/schemas/base-template';
import Decimal from 'decimal.js';
import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';
import {
  billing_run_merge_strategy,
  billing_run_invoice_source_filter,
} from '@trashlab/api/src/domains/billing/schemas/billing-enums';

export const surcharge_tax_applicability = pgEnum(
  'surcharge_tax_applicability',
  ['APPLY_TO_TAX', 'NO_TAX'],
);

export const payment_policy_state = pgEnum('payment_policy_state', [
  'ACTIVE',
  'ARCHIVED',
]);
export const invoice_trigger = pgEnum('invoice_trigger', [
  'ON_ORDER',
  'MANUAL',
  'BILLING_PERIOD',
]);
export const charge_trigger = pgEnum('charge_trigger', ['DELIVERY', 'PICKUP']);
export const custom_billing_period_unit = pgEnum('custom_billing_period_unit', [
  'DAYS',
  'WEEKS',
  'MONTHS',
]);
export const payment_methods = pgEnum('payment_methods', [
  'CASH',
  'CREDIT_CARD',
  'ACH',
  'CHECK',
]);

export const payment_policy = pgTable(
  'payment_policy',
  {
    ...defaultFields.fieldsWithTenantId(),
    state: payment_policy_state('state').notNull(),
    name: varchar('name', { length: 255 }).notNull(),
    invoiceTrigger: invoice_trigger('invoiceTrigger').notNull(),
    customBillingPeriodUnit: custom_billing_period_unit(
      'customBillingPeriodUnit',
    ),
    customBillingPeriod: integer('customBillingPeriod'),
    defaultDaysFromInvoiceGenerationToDue: integer(
      'defaultDaysFromInvoiceGenerationToDue',
    ),
    paymentMethods: payment_methods('paymentMethods').array().notNull(),
    customBillingPeriodStart: timestamp('customBillingPeriodStart', {
      mode: 'date',
    }),
    collectPaymentOnOrder: boolean('collectPaymentOnOrder'),
    description: text('description'),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    customBillingPeriodRecurrence: text('customBillingPeriodRecurrence'),
    chargeTrigger: charge_trigger('chargeTrigger'),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    creditCardSurchargePercentage: decimal('creditCardSurchargePercentage', {
      precision: 5,
      scale: 2,
    })
      .default(new Decimal(0))
      .notNull(),
    achSurchargePercentage: decimal('achSurchargePercentage', {
      precision: 5,
      scale: 2,
    })
      .default(new Decimal(0))
      .notNull(),

    surchargeTaxableApplicability: surcharge_tax_applicability(
      'surchargeTaxableApplicability',
    )
      .default('APPLY_TO_TAX')
      .notNull(),

    paymentSurchargeDescription: text('paymentSurchargeDescription'),
    isDefault: boolean('isDefault').default(false).notNull(),

    // Auto billing run functionality
    autoGenerateBillingRun: boolean('autoGenerateBillingRun')
      .default(false)
      .notNull(),

    // Auto-apply credits in billing runs
    autoApplyCredits: boolean('autoApplyCredits').default(true).notNull(),

    // Card expiration reminder defaults for customers in this billing group.
    cardExpirationReminderEnabled: boolean('cardExpirationReminderEnabled')
      .default(false)
      .notNull(),
    cardExpirationReminderDaysBefore: integer(
      'cardExpirationReminderDaysBefore',
    )
      .default(30)
      .notNull(),
    cardExpirationReminderFrequencyDays: integer(
      'cardExpirationReminderFrequencyDays',
    )
      .default(7)
      .notNull(),
    cardExpirationReminderMaxCount: integer('cardExpirationReminderMaxCount')
      .default(3)
      .notNull(),

    // Default billing run configuration fields
    defaultMergeStrategy: billing_run_merge_strategy(
      'defaultMergeStrategy',
    ).default('NO_MERGE'),
    defaultIncludeStatementOnInvoice: boolean(
      'defaultIncludeStatementOnInvoice',
    ).default(false),
    defaultInvoiceSourceFilter: billing_run_invoice_source_filter(
      'defaultInvoiceSourceFilter',
    ).default('SUBSCRIPTION_ONLY'),

    // Credit limit settings (billing group level)
    creditLimitPastDue: numeric('creditLimitPastDue'),
    creditLimitOpen: numeric('creditLimitOpen'),
    creditLimitAgingDays: integer('creditLimitAgingDays'),
  },
  (table) => {
    return {
      createdAtTs_idx: index('payment_policy_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      tenantId_idx: index('payment_policy_tenantId_idx').on(table.tenantId),
      updatedAtTs_idx: index('payment_policy_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      updatedAtTs_idx1: index('payment_policy_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('payment_policy_versionNumber_idx').on(
        table.versionNumber,
      ),
    };
  },
);

export const payment_policy_history = pgTable('payment_policy_history', {
  ...defaultHistoryFields.historyFieldsWithTenantId(),
  state: payment_policy_state('state'),
  name: varchar('name', { length: 255 }),
  invoiceTrigger: invoice_trigger('invoiceTrigger'),
  customBillingPeriodUnit: custom_billing_period_unit(
    'customBillingPeriodUnit',
  ),
  customBillingPeriod: integer('customBillingPeriod'),
  defaultDaysFromInvoiceGenerationToDue: integer(
    'defaultDaysFromInvoiceGenerationToDue',
  ),
  payment_methods: payment_methods('paymentMethods').array(),
  customBillingPeriodStart: timestamp('customBillingPeriodStart', {
    mode: 'string',
  }),
  collectPaymentOnOrder: boolean('collectPaymentOnOrder'),
  description: text('description'),
  lastModifiedBy: uuid('lastModifiedBy'),
  createdBy: uuid('createdBy'),
  customBillingPeriodRecurrence: text('customBillingPeriodRecurrence'),
  chargeTrigger: charge_trigger('chargeTrigger'),
  legacySystemId: varchar('legacySystemId', { length: 255 }),
  creditCardSurchargePercentage: decimal('creditCardSurchargePercentage', {
    precision: 5,
    scale: 2,
  }),
  achSurchargePercentage: decimal('achSurchargePercentage', {
    precision: 5,
    scale: 2,
  }),
  surchargeTaxableApplicability: surcharge_tax_applicability(
    'surchargeTaxableApplicability',
  ),
  paymentSurchargeDescription: text('paymentSurchargeDescription'),
  isDefault: boolean('isDefault'),

  // Auto billing run functionality in history
  autoGenerateBillingRun: boolean('autoGenerateBillingRun'),

  // Auto-apply credits in billing runs history
  autoApplyCredits: boolean('autoApplyCredits'),

  cardExpirationReminderEnabled: boolean('cardExpirationReminderEnabled'),
  cardExpirationReminderDaysBefore: integer('cardExpirationReminderDaysBefore'),
  cardExpirationReminderFrequencyDays: integer(
    'cardExpirationReminderFrequencyDays',
  ),
  cardExpirationReminderMaxCount: integer('cardExpirationReminderMaxCount'),

  // Default billing run configuration fields in history
  defaultMergeStrategy: billing_run_merge_strategy('defaultMergeStrategy'),
  defaultIncludeStatementOnInvoice: boolean('defaultIncludeStatementOnInvoice'),
  defaultInvoiceSourceFilter: billing_run_invoice_source_filter(
    'defaultInvoiceSourceFilter',
  ),

  // Credit limit settings (billing group level) in history
  creditLimitPastDue: numeric('creditLimitPastDue'),
  creditLimitOpen: numeric('creditLimitOpen'),
  creditLimitAgingDays: integer('creditLimitAgingDays'),
});
